import { decode } from 'base64-arraybuffer';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';

import { supabase } from '@/lib/supabase';

export async function submitFeatureRequest(userId: string, message: string, screenshotPath?: string | null): Promise<void> {
  const { error } = await supabase
    .from('feature_requests')
    .insert({ user_id: userId, message: message.trim(), screenshot_path: screenshotPath ?? null });
  if (error) throw error;
}

/** Επιλέγει εικόνα από τη συλλογή και την ανεβάζει στο ιδιωτικό bucket feedback-screenshots. Επιστρέφει το storage path (όχι public URL, το bucket είναι ιδιωτικό). */
export async function pickAndUploadFeedbackScreenshot(userId: string): Promise<string | null> {
  // Χωρίς requestMediaLibraryPermissionsAsync() — βλ. σχόλιο στο avatar.ts.
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    quality: 1,
  });
  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;

  const manipulated = await ImageManipulator.manipulateAsync(
    asset.uri,
    [{ resize: { width: 1080 } }],
    { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG, base64: true },
  );

  const path = `${userId}/${Date.now()}.jpg`;
  const { error } = await supabase.storage
    .from('feedback-screenshots')
    .upload(path, decode(manipulated.base64!), { contentType: 'image/jpeg' });
  if (error) throw error;

  return path;
}

// ── Admin ────────────────────────────────────────────────────────────────

export type FeatureRequestStatus = 'new' | 'read' | 'done';

export type FeatureRequestRow = {
  id: string;
  user_id: string;
  message: string;
  status: FeatureRequestStatus;
  created_at: string;
  screenshot_path: string | null;
};

/** Admin: λίστα bug reports/feature requests, πιο πρόσφατα πρώτα. RLS επιτρέπει μόνο σε admins. */
export async function fetchFeatureRequests(status?: FeatureRequestStatus | null): Promise<FeatureRequestRow[]> {
  let query = supabase
    .from('feature_requests')
    .select('id, user_id, message, status, created_at, screenshot_path')
    .order('created_at', { ascending: false });
  if (status) query = query.eq('status', status);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as FeatureRequestRow[];
}

export async function setFeatureRequestStatus(id: string, status: FeatureRequestStatus): Promise<void> {
  const { error } = await supabase.from('feature_requests').update({ status }).eq('id', id);
  if (error) throw error;
}

/** Admin: διαγράφει το request και το screenshot του (αν υπάρχει) από το storage. */
export async function deleteFeatureRequest(id: string, screenshotPath: string | null): Promise<void> {
  if (screenshotPath) {
    await supabase.storage.from('feedback-screenshots').remove([screenshotPath]).catch(() => {});
  }
  const { error } = await supabase.from('feature_requests').delete().eq('id', id);
  if (error) throw error;
}

export async function getFeedbackScreenshotUrl(path: string): Promise<string | null> {
  const { data } = await supabase.storage.from('feedback-screenshots').createSignedUrl(path, 3600);
  return data?.signedUrl ?? null;
}
